import { Typography } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Icon } from "../../../common/components";
import { Button } from "../../../common/components/Button";
import { usePageTitle } from "../../../common/hooks/usePageTitle";
import { useSelector } from "../../../common/hooks/useSelector";
import { createStyles, spacing } from "../../../styles/theme";
import { useCart } from "../../../utils/CartHooks";
import {
  canContinueToPayment,
  getReducedCartItems,
} from "../../../utils/CartUtils";
import { ROUTES } from "../../../constant/common.constant";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import CartComboItemSizeSelector from "../CartItemSizeSelector/CartComboItemSizeSelector";
import { productNameToProductLineAndFamily } from "../../../utils/product.utils";
import { trackCheckoutStarted } from "../../../utils/analytics.utils";
import HolidayWarning from "../../../components/HolidayWarning";

const styles = createStyles({
  header: {
    width: "100%",
    mb: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
  },
  emptyCart: {
    flex: "1 1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  cartItems: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: spacing.largeMobile, lg: spacing.largeDesktop },
  },
});

export const CartOverview = () => {
  const userInfos = useSelector((state) => state.userInfo);
  const { t, i18n } = useTranslation();
  const { showDistributorPrices } = useSelector((state) => state.cart);
  const tEnglish = i18n.getFixedT("en");
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoints();
  usePageTitle("pages.cart.title");
  const { isSaving } = useSelector((state) => state.cart);
  const { cart } = useSelector((state) => state.cart);
  const { getCurrency } = useCurrency();
  const { removeFromCart } = useCart();

  /**
   * Remove from cart action
   */
  const onItemRemove = useCallback(
    (id: string, productName: string) => {
      removeFromCart(id, getCurrency(), productName);
    },
    [removeFromCart, getCurrency]
  );

  const onContinueToShippingClick = useCallback(async () => {
    const { shippingTotal, orderTotal } = {
      shippingTotal:
        showDistributorPrices && !!cart.distributor
          ? cart.distributor?.shippingTotal
          : cart.shippingTotal,
      orderTotal:
        showDistributorPrices && !!cart.distributor
          ? cart.distributor?.orderTotal
          : cart.orderTotal,
    };
    trackCheckoutStarted({
      order_id: userInfos.id,
      value: orderTotal,
      revenue: orderTotal - shippingTotal,
      shipping: shippingTotal,
      currency: cart.currency,
      products: getReducedCartItems(cart?.comboItems).map((itemPricing) => {
        const { family, productLine } = productNameToProductLineAndFamily(
          itemPricing.productName
        );
        const pricePerUnit =
          showDistributorPrices && !!itemPricing.distributor
            ? itemPricing.distributor?.pricePerUnit
            : itemPricing.pricePerUnit;

        return {
          product_id: itemPricing.productName,
          sku: itemPricing.designId,
          name: itemPricing.productName,
          category: family?.labelTag ? tEnglish(family?.labelTag) : "",
          brand: tEnglish(`line.${productLine}.title`),
          price: pricePerUnit,
          quantity: itemPricing.quantity,
        };
      }),
    });

    navigate(ROUTES.CHECKOUT);
  }, [navigate, cart, userInfos, tEnglish, showDistributorPrices]);

  return (
    <Box key="sizeCollection">
      <Box sx={styles.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h1">{t("checkout.title")}</Typography>
          <a
            href={t("checkout.size_link")}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button
              color="primary"
              size="small"
              startIcon={<Icon icon="north_east" size={18} color="white" />}
              sx={{
                borderRadius: { xs: "4px", lg: "8px" },
              }}
            >
              {t("checkout.view_sizes")}
            </Button>
          </a>
        </Box>
        <HolidayWarning
          boxProps={{
            mt: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
          }}
        />
      </Box>
      <Box sx={styles.cartItems} key="cartItems">
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
        >
          {cart.comboItems.map((comboItem) => (
            <Box key={comboItem.items[0].cartItemId}>
              <CartComboItemSizeSelector
                comboItems={comboItem}
                onRemove={onItemRemove}
              />
            </Box>
          ))}
        </Box>
      </Box>
      {isMobile && (
        <Button
          sx={{ mt: spacing.largeMobile }}
          disabled={
            !canContinueToPayment(isSaving, cart.comboItems, cart, userInfos)
          }
          onClick={onContinueToShippingClick}
          fullWidth
          data-test="cart.continue"
        >
          {t("general.continue")}
        </Button>
      )}
    </Box>
  );
};
